import React, { useState, useEffect } from "react";
import Loader from "../../loader/Loader";
import { toast } from "react-toastify";

const AddDynamicCategory = ({
  categories, // List of all available categories
  brands: allAvailableBrands, // List of all available brands (renamed for clarity)
  categoryBrandMap, // Pass the current map to filter already associated brands (this is already handled)
  onBrandAdded, // Function to call on submit (receives category, selectedBrands)
  onClose, // Function to close the modal
  isLoading, // Loading state from the parent/hook
}) => {
  const [selectedCategory, setSelectedCategory] = useState(""); // Store selected category
  const [formData, setFormData] = useState({ specs: [] }); // Store form data for specifications
  const [error, setError] = useState(""); // Error message state

  // Reset brands when category changes or modal opens/closes
  useEffect(() => {
    setFormData({ specs: [] }); // Clear all specs when category changes
  }, [selectedCategory]);

  // Handle category change
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  // Handle brand selection for specifications
  const handleSpecsChange = (index, value) => {
    const newSpecs = [...formData.specs];
    newSpecs[index] = { brand: value };
    setFormData({ ...formData, specs: newSpecs });
  };

  // Add new specification field
  const addField = () => {
    setFormData({
      ...formData,
      specs: [...formData.specs, { brand: "" }],
    });
  };

  // Remove specification field
  const removeField = (index) => {
    const newSpecs = formData.specs.filter((_, i) => i !== index);
    setFormData({ ...formData, specs: newSpecs });
  };

  // Handle submit
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(""); // Clear previous errors

    if (!selectedCategory) {
      setError("Please select a category.");
      return;
    }

    // Validate selected brands for all specifications
    const selectedBrands = formData.specs.map((spec) => spec.brand);
    if (selectedBrands.length === 0 || selectedBrands.includes("")) {
      setError("Please select at least one brand for each specification.");
      return;
    }

    // Basic check for duplicate selections within the current form
    const uniqueSelectedBrands = new Set(selectedBrands);
    if (uniqueSelectedBrands.size !== selectedBrands.length) {
      setError("Please select unique brands for each specification.");
      return;
    }

    // Call the parent's function with the selected category and brands
    try {
      // Ensure the selected brands for this category are unique based on existing ones
      const existingBrands = categoryBrandMap[selectedCategory] || [];
      const brandsToAdd = selectedBrands.filter(
        (brand) => !existingBrands.includes(brand)
      );

      if (brandsToAdd.length === 0) {
        setError(
          "All selected brands are already associated with this category."
        );
        return;
      }

      await onBrandAdded(selectedCategory, brandsToAdd);

      onClose(); // Close modal on success
    } catch (e) {
      setError(e?.message || "An unexpected error occurred during submission.");
    }
  };

  // Filter the brands that are *not* already associated with the selected category
  // and also exclude brands already selected in *other* specification fields in this form
  const getAvailableBrandsForSpec = (currentIndex) => {
    const selectedBrandsInForm = formData.specs
      .map((spec) => spec.brand)
      .filter((brand, index) => brand && index !== currentIndex); // Get brands selected in *other* specs

    const existingBrandsForCategory = categoryBrandMap[selectedCategory] || [];

    return allAvailableBrands.filter(
      (brand) =>
        !existingBrandsForCategory.includes(brand) && // Exclude brands already linked to category
        !selectedBrandsInForm.includes(brand) // Exclude brands already selected in other fields
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center rounded-lg z-10">
            <Loader />
          </div>
        )}

        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-800 rounded-full hover:bg-gray-200"
          aria-label="Close modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-2xl font-semibold mb-4 text-gray-700">
          Add Brands to{" "}
          <span className="text-orange-600">
            {selectedCategory || "a Category"}
          </span>
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Category Selection */}
          <div className="mb-4">
            <label
              htmlFor="categorySelect"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Select Category
            </label>
            <select
              id="categorySelect"
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="border py-2 px-3 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              disabled={isLoading}
              required // Make category selection required
            >
              <option value="">-- Select a Category --</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Brand Selection for Specifications */}
          {selectedCategory && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Select Brands for Specifications
              </label>
              <div className="space-y-3">
                {formData.specs.map((spec, index) => {
                  const brandsForThisSpec = getAvailableBrandsForSpec(index);
                  return (
                    <div key={index} className="flex items-center space-x-2">
                      <select
                        value={spec.brand}
                        onChange={(e) =>
                          handleSpecsChange(index, e.target.value)
                        }
                        className="flex-1 block w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Select Brand</option>
                        {brandsForThisSpec.map((brand) => (
                          <option key={brand} value={brand}>
                            {brand}
                          </option>
                        ))}
                      </select>
                      {formData.specs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeField(index)}
                          className="text-red-500 hover:text-red-700 text-sm p-1"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={addField}
                  // Disable "Add Specification" if no more brands are available to select
                  disabled={getAvailableBrandsForSpec(-1).length === 0}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium disabled:text-gray-400"
                >
                  + Add Specification
                </button>
              </div>
            </div>
          )}

          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

          <div className="mt-6">
            <button
              type="submit"
              disabled={
                isLoading ||
                !selectedCategory ||
                formData.specs.length === 0 ||
                formData.specs.some((spec) => spec.brand === "")
              }
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white py-2 px-4 rounded-md font-semibold transition duration-200"
            >
              {isLoading ? "Adding..." : "Add Brands"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDynamicCategory;
